import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  confirmEvent,
  fetchEventConfirmation,
  type ConfirmEventResult,
  type EventConfirmationState,
} from '../api/events';

export function eventConfirmationQueryKey(eventId: string) {
  return ['event-confirmation', eventId] as const;
}

export type UseEventConfirmationState = {
  state: EventConfirmationState | null;
  loading: boolean;
  refreshing: boolean;
  submitting: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  confirmWithoutSignature: (reason: string) => Promise<ConfirmEventResult>;
};

export default function useEventConfirmationState(
  eventId: string,
  options?: { enabled?: boolean },
): UseEventConfirmationState {
  const queryClient = useQueryClient();
  const enabled = Boolean(eventId) && options?.enabled !== false;
  const confirmationQuery = useQuery({
    queryKey: eventConfirmationQueryKey(eventId),
    enabled,
    queryFn: () => fetchEventConfirmation(eventId),
    staleTime: 15_000,
    // Confirmation can change in another manager's Home task. Always refresh
    // when this panel/modal mounts so a recently cached pending state is never
    // presented as current evidence.
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      const status = typeof error === 'object' && error != null && 'status' in error
        ? Number((error as { status?: unknown }).status)
        : null;
      return status === 404 || status === 403 ? false : failureCount < 1;
    },
  });
  const confirmationMutation = useMutation({
    mutationFn: (reason: string) => confirmEvent(eventId, {
      reason,
      acknowledgeNoClientSignature: true,
    }),
    onSuccess: (result) => {
      queryClient.setQueryData(eventConfirmationQueryKey(eventId), result.confirmation);
    },
  });

  const queryError = confirmationQuery.error instanceof Error
    ? confirmationQuery.error.message
    : confirmationQuery.error
      ? 'Unable to load confirmation details.'
      : null;
  const mutationError = confirmationMutation.error instanceof Error
    ? confirmationMutation.error.message
    : confirmationMutation.error
      ? 'Unable to confirm this event.'
      : null;

  return {
    state: confirmationQuery.data ?? null,
    loading: enabled && confirmationQuery.isLoading,
    refreshing: confirmationQuery.isFetching && !confirmationQuery.isLoading,
    submitting: confirmationMutation.isPending,
    error: mutationError ?? queryError,
    refresh: async () => {
      await confirmationQuery.refetch();
    },
    confirmWithoutSignature: confirmationMutation.mutateAsync,
  };
}
